/**
 * Agents page at /it/agents.
 *
 * Landing page for the agent platform: per-agent cards on top, then a
 * sortable/filterable/exportable runs table.
 *
 * Standard cockpit list-page composition per COCKPIT.md rule 2.7:
 *   - Sortable columns use the shared SortableHeader.
 *   - Multi-select filters use SearchableSelect (under the 15-option
 *     threshold so the search input stays hidden).
 *   - Sort state lives in the URL query string so refreshes stay stable.
 *   - Excel export is a CSV download (small dataset, no server round-trip).
 *
 * Back link top-left per .claude/rules/cockpit-ui-navigation.md.
 */

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, Bot, Download, RefreshCw } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';

import { SortableHeader, gridTheadClassName, type SortState } from '@ampersandbrands/ui';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Pill } from '@/components/Pill';
import { FilterLabel } from '@/components/FilterLabel';
import { DataGridShell } from '@/components/grid/DataGridShell';
import {
  formatDuration,
  statusLabel,
  useAgentRuns,
  type AgentDefinition,
  type AgentRun,
} from '@/lib/useAgents';
import { relativeTime } from '@/lib/useCockpitOps';

const STAGE_OPTIONS = [
  { value: 'research', label: 'Research' },
  { value: 'fix', label: 'Fix' },
  { value: 'qa', label: 'QA' },
];

const STATUS_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'failure', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'cancelled', label: 'Cancelled' },
];

type SortKey = 'started' | 'duration' | 'stage' | 'status' | 'issue';

function statusBucket(run: AgentRun): string {
  if (run.status !== 'completed') return 'running';
  if (run.conclusion === 'success') return 'success';
  if (run.conclusion === 'failure') return 'failure';
  if (run.conclusion === 'cancelled') return 'cancelled';
  return run.conclusion ?? 'unknown';
}

function AgentCard({ agent }: { agent: AgentDefinition }) {
  const link = `/it/agents/${agent.slug}`;
  const isPlanned = agent.status === 'planned';
  const cap = agent.daily_cap_usd;
  const today = agent.spend_today_usd;
  const pct = cap !== null && today !== null && cap > 0 ? Math.min(100, (today / cap) * 100) : 0;
  return (
    <Link
      to={link}
      className="block rounded-lg border border-amp-line bg-amp-white p-4 shadow-1 hover:shadow-2 transition-shadow dark:border-amp-dark-line dark:bg-amp-dark-surface"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
          <h2 className="text-base font-semibold text-amp-ink dark:text-amp-dark-ink">
            {agent.name}
          </h2>
        </div>
        {isPlanned ? (
          <Pill tone="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
            Planned
          </Pill>
        ) : (
          <Pill tone="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
            Live
          </Pill>
        )}
      </div>
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted mb-3 line-clamp-2">
        {agent.description}
      </p>
      <div className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <div className="flex items-baseline justify-between mb-1">
          <span>Today vs daily cap</span>
          <span className="font-medium text-amp-ink dark:text-amp-dark-ink">
            {today !== null ? `$${today.toFixed(2)}` : '—'} /{' '}
            {cap !== null ? `$${cap.toFixed(2)}` : '—'}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-amp-cream-warm dark:bg-amp-dark-surface-warm overflow-hidden">
          <div
            className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-amp-olive dark:bg-amp-olive-dark'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </Link>
  );
}

function repoShortName(repository: string): string {
  const slash = repository.lastIndexOf('/');
  return slash >= 0 ? repository.slice(slash + 1) : repository;
}

function RepoBadge({ repository }: { repository: string }) {
  return (
    <Pill
      title={repository}
      className="uppercase tracking-caps-pill bg-amp-cream-warm text-amp-ink-muted border-amp-cream-warm dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted dark:border-transparent"
    >
      {repoShortName(repository)}
    </Pill>
  );
}

function StagePill({ stage }: { stage: AgentRun['agent'] }) {
  const palette: Record<AgentRun['agent'], string> = {
    agent: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    research: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    fix: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    qa: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    unknown: 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
  };
  return (
    <Pill tone={palette[stage]} className="capitalize">
      {stage}
    </Pill>
  );
}

function RunStatusPill({ run }: { run: AgentRun }) {
  const { label, tone } = statusLabel(run);
  const tones: Record<typeof tone, string> = {
    ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    err: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    idle: 'bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted',
  };
  return <Pill tone={tones[tone]}>{label}</Pill>;
}

// CSV download. Small dataset, no need for a server-side xlsx endpoint.
function exportRunsCsv(rows: AgentRun[]): void {
  const header = ['Repo', 'Issue', 'Issue title', 'Stage', 'Status', 'Started', 'Duration (s)', 'Run URL'];
  const escape = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    header.join(','),
    ...rows.map((r) =>
      [
        repoShortName(r.repository),
        r.issue_number ?? '',
        r.issue_title ?? '',
        r.agent,
        statusBucket(r),
        r.started_at,
        r.duration_seconds ?? '',
        r.html_url,
      ]
        .map(escape)
        .join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `agent-runs-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function Agents() {
  const { agents, recentRuns, isLoading, isFetching, stale, refetch } = useAgentRuns();
  const [searchParams, setSearchParams] = useSearchParams();

  const stageFilter = (searchParams.get('stage') ?? '').split(',').filter(Boolean);
  const statusFilter = (searchParams.get('status') ?? '').split(',').filter(Boolean);
  const sortKey = (searchParams.get('sort') as SortKey) || 'started';
  const sortDir: SortState = (searchParams.get('dir') as SortState) ?? 'desc';

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setParam('sort', key);
      setParam('dir', 'desc');
      return;
    }
    if (sortDir === 'desc') setParam('dir', 'asc');
    else if (sortDir === 'asc') {
      setParam('sort', null);
      setParam('dir', null);
    } else setParam('dir', 'desc');
  };

  const sortedFiltered = useMemo(() => {
    const filtered = recentRuns.filter((r) => {
      if (stageFilter.length > 0 && !stageFilter.includes(r.agent)) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(statusBucket(r))) return false;
      return true;
    });
    if (!sortKey || !sortDir) return filtered;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'started':
          return dir * (Date.parse(a.started_at) - Date.parse(b.started_at));
        case 'duration':
          return dir * ((a.duration_seconds ?? -1) - (b.duration_seconds ?? -1));
        case 'stage':
          return dir * a.agent.localeCompare(b.agent);
        case 'status':
          return dir * statusBucket(a).localeCompare(statusBucket(b));
        case 'issue':
          return dir * ((a.issue_number ?? 0) - (b.issue_number ?? 0));
        default:
          return 0;
      }
    });
  }, [recentRuns, stageFilter, statusFilter, sortKey, sortDir]);

  const sortStateFor = (k: SortKey): SortState => (sortKey === k ? sortDir : null);

  const runsToolbar = (
    <div>
      <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-2">
        Last 50 across all agent workflows on this repo. Refreshes every 30s.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <FilterButton
          label="Stage"
          options={STAGE_OPTIONS}
          selected={stageFilter}
          onChange={(next) => setParam('stage', next.join(',') || null)}
        />
        <FilterButton
          label="Status"
          options={STATUS_OPTIONS}
          selected={statusFilter}
          onChange={(next) => setParam('status', next.join(',') || null)}
        />
        <button
          type="button"
          onClick={() => exportRunsCsv(sortedFiltered)}
          disabled={sortedFiltered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          Export
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            IT - Agents
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">
            Agent platform
          </h1>
          <p className="mt-1 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted max-w-2xl">
            Read-only view of the Claude-powered agents running against this org. Labels on GitHub issues drive the pipeline; this page surfaces history and spend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToolPinToggle toolSlug="agents" toolName="Agents" />
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-amp-line bg-amp-white px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {stale ? (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          GitHub upstream slow or unreachable; showing last cached data.
        </div>
      ) : null}

      <div className="grid gap-3 mb-4 sm:grid-cols-2">
        {agents.length === 0 && isLoading ? (
          <div className="col-span-2 rounded-lg border border-amp-line bg-amp-white p-6 text-sm text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink-muted">
            Loading agents…
          </div>
        ) : (
          agents.map((a) => <AgentCard key={a.slug} agent={a} />)
        )}
      </div>

      <DataGridShell
        title="Recent runs"
        loaded={sortedFiltered.length}
        countNoun="runs"
        toolbar={runsToolbar}
      >
        <table className="w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <SortableHeader label="Issue" sorted={sortStateFor('issue')} onToggle={() => toggleSort('issue')} />
              <SortableHeader label="Stage" sorted={sortStateFor('stage')} onToggle={() => toggleSort('stage')} />
              <SortableHeader label="Status" sorted={sortStateFor('status')} onToggle={() => toggleSort('status')} />
              <SortableHeader label="Started" sorted={sortStateFor('started')} onToggle={() => toggleSort('started')} />
              <SortableHeader label="Duration" sorted={sortStateFor('duration')} onToggle={() => toggleSort('duration')} align="right" />
              <th className="px-4 py-2 text-left font-medium">Run</th>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  {isLoading ? 'Loading runs…' : 'No agent runs match the current filters.'}
                </td>
              </tr>
            ) : (
              sortedFiltered.map((run) => (
                <tr key={run.id} className="border-t border-amp-line dark:border-amp-dark-line">
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      <RepoBadge repository={run.repository} />
                      {run.issue_number !== null ? (
                        <a
                          href={`https://github.com/${run.repository}/issues/${run.issue_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={run.issue_title ?? `Issue #${run.issue_number}`}
                          className="text-amp-olive-deep hover:underline dark:text-amp-olive-dark font-medium"
                        >
                          #{run.issue_number}
                        </a>
                      ) : (
                        <span
                          title={run.issue_title ?? run.head_branch ?? ''}
                          className="text-amp-ink-muted dark:text-amp-dark-ink-muted"
                        >
                          {run.issue_title ? run.issue_title.slice(0, 40) + (run.issue_title.length > 40 ? '…' : '') : run.head_branch || '—'}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2"><StagePill stage={run.agent} /></td>
                  <td className="px-4 py-2"><RunStatusPill run={run} /></td>
                  <td className="px-4 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
                    {relativeTime(run.started_at)}
                  </td>
                  <td className="px-4 py-2 text-right text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
                    {formatDuration(run.duration_seconds)}
                  </td>
                  <td className="px-4 py-2">
                    <a
                      href={run.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-amp-olive-deep hover:underline dark:text-amp-olive-dark"
                    >
                      Open <ArrowUpRight className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

type FilterButtonProps = {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
};

function FilterButton({ label, options, selected, onChange }: FilterButtonProps) {
  // Wraps SearchableSelect with a labeled column header. Below the 15-option
  // threshold so the search input stays hidden.
  return (
    <div className="flex flex-col gap-1">
      <FilterLabel as="div" className="!mb-0">
        {label}
      </FilterLabel>
      <SearchableSelect
        options={options}
        selected={selected}
        onChange={onChange}
        multiSelect
        placeholder="Any"
      />
    </div>
  );
}
